import React, { useState } from 'react';
import { ApolloProvider, gql, useQuery } from '@apollo/client';
import client from '../lib/apolloClient';
import DealTeamWorkspacePage from '../components/Succeedo/DealTeam/DealTeamWorkspacePage';
import DealTeamUploadModal from '../components/Succeedo/DealTeam/DealTeamUploadModal';
import ShareSnapshotModal from '../components/Succeedo/DealTeam/ShareSnapshotModal';
import MilestoneCompletionModal from '../components/Succeedo/DealTeam/MilestoneCompletionModal';
import AppLayout from '../components/Succeedo/Layout/AppLayout';

const GET_WORKSPACE_DATA = gql`
  query DealTeamWorkspace($id: ID!) {
    currentUser {
      id
      name
      province
      industry_sector
      advisory_team_members {
        id
        role
        status
        name
        firm
      }
    }
    deal_team(id: $id) {
      id
      name
      description
      status
      conversation_id
      owner_user_id
      viewer
      members {
        id
        role
        status
        name
        email
        coach_profile_id
        advisory_team_member_id
        user_id
        accepted_at
      }
      progress {
        assessment
        valuation
        financing
        deal_structure
        legal_review
        letter_of_intent
        close
      }
    }
    deal_team_vault_documents(deal_team_id: $id) {
      id
      user_id
      title
      file_name
      file_size
      download_url
      shared_with_roles
      uploaded_by_name
      created_at
      source_type
      source_id
      note
    }
  }
`;

function SucceedoDealTeamWorkspaceWithData({ dealTeamId }) {
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [shareSnapshotOpen, setShareSnapshotOpen] = useState(false);
  const [milestoneModalId, setMilestoneModalId] = useState(null);

  const { data, loading, error, refetch } = useQuery(GET_WORKSPACE_DATA, {
    variables: { id: dealTeamId },
    fetchPolicy: 'cache-and-network',
  });

  const currentUser = data?.currentUser || null;
  const team = data?.deal_team || null;
  const members = team?.members || [];
  const documents = data?.deal_team_vault_documents || [];

  if (loading && !data) {
    return (
      <AppLayout breadcrumb="DEAL TEAM WORKSPACE">
        <div className="flex items-center justify-center p-12">
          <div className="text-[hsl(var(--muted-foreground))] text-[16px]">
            Loading workspace…
          </div>
        </div>
      </AppLayout>
    );
  }

  if (error || !team) {
    return (
      <AppLayout breadcrumb="DEAL TEAM WORKSPACE">
        <div className="max-w-2xl mx-auto p-12 text-center">
          <h2 className="font-serif text-[24px] font-light text-[hsl(var(--foreground))] mb-2">
            Deal team not found
          </h2>
          <p className="text-[hsl(var(--muted-foreground))] text-[14px]">
            This deal team doesn't exist, or you don't have access to it.
          </p>
        </div>
      </AppLayout>
    );
  }

  const handleAddRole = (_role) => {
    // V1: navigate back to TeamBuilder for now; deeper modal integration is Unit 7's scope.
    window.location.href = '/succeedo/teams';
  };

  const handleOpenUpload = () => setUploadModalOpen(true);
  const handleCloseUpload = () => setUploadModalOpen(false);

  const handleOpenShareSnapshot = () => setShareSnapshotOpen(true);
  const handleCloseShareSnapshot = () => setShareSnapshotOpen(false);

  // After a successful upload+share, refetch the workspace so the new doc
  // appears in the Documents tab. The modal closes itself on success.
  const handleUploadSuccess = () => {
    if (typeof refetch === 'function') refetch();
  };

  // Same refetch path for snapshot shares — a successful share creates a
  // new VaultDocument that should show up in the Documents tab.
  const handleShareSnapshotSuccess = () => {
    if (typeof refetch === 'function') refetch();
  };

  // Manage-access edits change `shared_with_roles` on an existing doc;
  // refetch so the Documents tab badges reflect the new state.
  const handleAccessChanged = () => {
    if (typeof refetch === 'function') refetch();
  };

  // Manual-checkpoint milestone (legal_review / letter_of_intent / close).
  // Owner-only; the button affordance is rendered conditionally inside
  // DealProgressTracker. After a successful mark, refetch so the Progress
  // tab booleans + status banner reflect the new state.
  const handleOpenMilestoneModal = (milestoneId) => setMilestoneModalId(milestoneId);
  const handleCloseMilestoneModal = () => setMilestoneModalId(null);
  const handleMilestoneMarked = () => {
    if (typeof refetch === 'function') refetch();
  };

  return (
    <AppLayout breadcrumb="DEAL TEAM WORKSPACE">
      <DealTeamWorkspacePage
        team={team}
        members={members}
        documents={documents}
        viewer={team?.viewer}
        currentUserId={currentUser?.id}
        currentUserName={currentUser?.name}
        onAddRole={handleAddRole}
        onUploadDocument={handleOpenUpload}
        onShareSnapshot={handleOpenShareSnapshot}
        onAccessChanged={handleAccessChanged}
        onMarkMilestone={handleOpenMilestoneModal}
      />
      <DealTeamUploadModal
        open={uploadModalOpen}
        onClose={handleCloseUpload}
        onSuccess={handleUploadSuccess}
        dealTeamId={dealTeamId}
        dealTeamName={team?.name}
      />
      <ShareSnapshotModal
        open={shareSnapshotOpen}
        onClose={handleCloseShareSnapshot}
        onSuccess={handleShareSnapshotSuccess}
        dealTeamId={dealTeamId}
        dealTeamName={team?.name}
      />
      <MilestoneCompletionModal
        open={milestoneModalId != null}
        onClose={handleCloseMilestoneModal}
        onSuccess={handleMilestoneMarked}
        dealTeamId={dealTeamId}
        milestoneId={milestoneModalId}
        documents={documents}
      />
    </AppLayout>
  );
}

const SucceedoDealTeamWorkspaceContainer = (props, _railsContext) => (
  <ApolloProvider client={client}>
    <SucceedoDealTeamWorkspaceWithData dealTeamId={props.dealTeamId} />
  </ApolloProvider>
);

export default SucceedoDealTeamWorkspaceContainer;
